import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import isNil from "lodash/isNil";
import Box from "@mui/material/Box";

import AgGridTableWrapper from "../../../components/agGrid/AgGridTableWrapper";
import CellRendererLoanName from "../../../components/agGrid/cellRenderer/CellRendererLoanName";
import OpenInNewTabButton from "../../../components/ui/buttons/OpenInNewTabButton";
import SearchInput from "../../../components/inputs/SearchInput";
import FilterTabSwitcher from "../../../components/navigation/FilterTabSwitcher";
import { PIPELINE_TABS } from "../../../constants/pipelineTabs";

import { T } from "../../SupportScreen/components/supportUi";
import { formatDate as sharedFormatDate } from "../../../_src_shared/utils/functions/formatDate";
import { level1 } from "../../../theme/colors";
import {
  ADMIN_GRID_SX,
  GRID_HEADER_H,
  GRID_ROW_H,
} from "../../admin/shared/adminGrid";

// A loan row's page: the feed's own `link` (`/loan/{id}` pre-close,
// `/acquisition/{id}` for an acquisition); the pre-close cache rows have none.
const loanLink = (row) => row?.link || `/loan/${row?._id}`;

const PIPELINE_LABEL = {
  Underwriting: "Pre-close",
  Acquisitions: "Acquisitions",
};

// All loan statuses across the pipeline boards — pre-close + closing (+ post-
// close) combined — so the status filter covers every stage a correspondent's
// loans can be in. Deduped: Pre-Close lists every status (it's the full board),
// so its chips already overlap the Closing / Post-Close ones.
const ALL_STATUS_OPTIONS = [
  ...new Set([
    ...PIPELINE_TABS.PRE_CLOSE.statuses,
    ...PIPELINE_TABS.CLOSING.statuses,
    ...PIPELINE_TABS.POST_CLOSE.statuses,
  ]),
];

const usdWhole = (n) =>
  n == null || !Number.isFinite(Number(n))
    ? "—"
    : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const fmtDate = (v) => {
  if (!v) return "—";
  return sharedFormatDate(v, String(v), "MMM d, yyyy");
};

/**
 * The correspondent loans table — used both in the correspondent drilldown
 * Loans tab and inline in the correspondents list's expanded row, so the two
 * render identically. loanNumber / loanName coldefs mirror the pipeline tables
 * (useColumnDefsLoanPipelineTable).
 *
 * - `embedded` (list expand row): auto-height grid (no fixed height), smaller
 *   page size, no toolbar.
 * - Otherwise a fixed-height scrolling grid (drilldown) with the same
 *   search + status-filter toolbar the pipeline tables have.
 */
export default function CorrespondentLoansGrid({
  rows,
  embedded = false,
  loading = false,
  showToolbar,
}) {
  const navigate = useNavigate();
  const withToolbar = showToolbar ?? !embedded;

  const [quickFilter, setQuickFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);

  // "All" + every combined pipeline status (pre-close + closing + post-close),
  // each with a count. Any status present on a loan but not in the standard set
  // is appended so no loan is unreachable.
  const statusTabs = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    const counts = {};
    list.forEach((r) => {
      const s = r?.loanStatus;
      if (s) counts[s] = (counts[s] || 0) + 1;
    });
    const extras = Object.keys(counts).filter(
      (s) => !ALL_STATUS_OPTIONS.includes(s),
    );
    const ordered = [...ALL_STATUS_OPTIONS, ...extras];
    return [
      { label: "All", value: null, count: list.length },
      ...ordered.map((s) => ({ label: s, value: s, count: counts[s] || 0 })),
    ];
  }, [rows]);

  const displayRows = useMemo(() => {
    if (!statusFilter) return rows;
    return (Array.isArray(rows) ? rows : []).filter(
      (r) => r?.loanStatus === statusFilter,
    );
  }, [rows, statusFilter]);

  const cols = useMemo(
    () => [
      {
        headerName: "Loan #",
        field: "loanNumber",
        minWidth: 90,
        maxWidth: 90,
        width: 90,
        suppressAutoSize: true,
        suppressHeaderFilterButton: true,
        flex: 0,
        getQuickFilterText: (params) =>
          params.value ? String(params.value) : "",
        filter: "agTextColumnFilter",
        cellRenderer: (params) => (
          <OpenInNewTabButton linkIcon to={loanLink(params?.data)}>
            {params?.data?.loanNumber}
          </OpenInNewTabButton>
        ),
        suppressFloatingFilterButton: true,
      },
      {
        headerName: "Loan Name",
        field: "loanName",
        // Flex (not fixed width) so the columns fill the container — no empty
        // space on the right, and the long loan name gets the most room.
        flex: 2,
        minWidth: 240,
        // Compact: one line (name + chevron) so every row keeps the admin
        // table's fixed row height.
        cellRenderer: CellRendererLoanName,
        cellRendererParams: { compact: true },
        valueGetter: (params) =>
          isNil(params?.data?.loanName)
            ? `Loan #${params?.data?.loanNumber}`
            : params?.data?.loanName,
        suppressHeaderFilterButton: true,
        suppressFloatingFilterButton: true,
        headerTooltip: "Go to loan",
        cellStyle: { cursor: "pointer" },
      },
      {
        // Which pipeline the loan lives in (only the both-pipelines feed says).
        headerName: "Pipeline",
        colId: "pipeline",
        flex: 0.8,
        minWidth: 110,
        valueGetter: (p) => PIPELINE_LABEL[p.data?.pipeline] || "Pre-close",
      },
      { headerName: "Status", field: "loanStatus", flex: 1, minWidth: 120 },
      {
        headerName: "Product",
        field: "loanProductType",
        flex: 1,
        minWidth: 120,
      },
      {
        headerName: "Amount",
        field: "totalLoanAmount",
        flex: 0.8,
        minWidth: 110,
        // Monospaced, right-aligned figures (ADMIN_GRID_SX `.num`).
        cellClass: "num",
        headerClass: "num",
        valueFormatter: (p) => usdWhole(p.value ?? p.data?.baseLoanAmount),
      },
      {
        // The loan's key date: expected closing on a pre-close loan; settlement
        // (actual → expected) on an acquisition.
        headerName: "Closing / Settlement",
        colId: "keyDate",
        flex: 0.9,
        minWidth: 140,
        valueGetter: (p) =>
          p.data?.keyDate ?? p.data?.expectedClosingDate ?? null,
        valueFormatter: (p) => fmtDate(p.value),
      },
    ],
    [],
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
      {withToolbar && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1,
            mb: 1.25,
          }}
        >
          <SearchInput
            value={quickFilter}
            onChange={setQuickFilter}
            placeholder="Search loans…"
            rounded
            size="small"
            sx={{ width: 220 }}
          />
          {statusTabs.length > 1 && (
            <Box sx={{ display: "flex", alignItems: "center", ml: 0.5 }}>
              <Box
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#000000d6",
                  mr: 0.5,
                  flexShrink: 0,
                }}
              >
                Status:
              </Box>
              <FilterTabSwitcher
                value={statusFilter}
                onChange={setStatusFilter}
                tabs={statusTabs}
                fontSize={11}
                fontWeight={400}
                activeFontWeight={500}
                activeColor={level1.text}
                color="#181d1f"
                backgroundColor="transparent"
              />
            </Box>
          )}
        </Box>
      )}

      {/* The house table look (the admin / pricing tables): an 8px-rounded
          hairline block, faint small-caps headers on no fill, hairline row
          rules, 36px rows, monospaced figures. The border lives on this box so
          it can't stack with the grid's own. */}
      <Box
        sx={{
          border: `1px solid ${T.line}`,
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <AgGridTableWrapper
          compactTable
          noBorder
          sx={{
            ...ADMIN_GRID_SX,
            height: embedded ? "auto" : 520,
            ".ag-cell": {
              ...ADMIN_GRID_SX[".ag-cell"],
              display: "flex",
              alignItems: "center",
            },
            ".ag-row": { ...ADMIN_GRID_SX[".ag-row"], cursor: "pointer" },
          }}
        >
          <AgGridReact
            rowData={displayRows}
            columnDefs={cols}
            defaultColDef={{
              resizable: true,
              sortable: true,
              minWidth: 90,
              suppressHeaderMenuButton: true,
            }}
            getRowId={(p) => String(p.data?._id ?? "")}
            quickFilterText={quickFilter}
            loading={loading}
            domLayout={embedded ? "autoHeight" : "normal"}
            pagination
            paginationPageSize={embedded ? 8 : 25}
            rowHeight={GRID_ROW_H}
            headerHeight={GRID_HEADER_H}
            onRowClicked={(e) => {
              if (e.data?._id) navigate(loanLink(e.data));
            }}
          />
        </AgGridTableWrapper>
      </Box>
    </Box>
  );
}
